/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    getMediaLocationsForProductAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric,
    MediaLocationsInput
} from '@msdyn365-commerce/core';
import { getMediaLocationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { MediaLocation } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

import { QueryResultSettingsProxy, generateImageUrl, generateCacheKey } from '@msdyn365-commerce-modules/retail-actions';
import { ProductsDataActions } from '@msdyn365-commerce/retail-proxy';

/**
 * Product Input.
 */
export class MediaLocationsByIdInput implements IActionInput {
    public productId?: number;

    public channelId: number;

    public catalogId: number;

    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings, productId?: number, catalogId?: number) {
        this.productId = productId;
        this.channelId = +apiSettings.channelId;
        this.catalogId = catalogId || 0;
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () =>
        generateCacheKey(
            this.apiSettings,
            { key: 'Key', value: 'MediaLocationID' },
            { key: 'ProductId', value: this.productId },
            { key: 'CatalogId', value: this.catalogId }
        );

    public getCacheObjectType = () => 'MediaLocations';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData
 */
export const createMediaLocationsByIdInput = (inputData: ICreateActionContext<IGeneric<IAny>>): MediaLocationsByIdInput => {
    const productId = (inputData.config && +inputData.config.productId) || undefined;
    const catalogId = getCatalogId(inputData.requestContext);
    return new MediaLocationsByIdInput(inputData.requestContext.apiSettings, productId, catalogId);
};

/**
 * Calls the Retail API and returns the product based on the passed MediaLocationsByIdInput.
 * @param input
 * @param ctx
 */
export async function getMediaLocationsByIdAction(input: MediaLocationsByIdInput, ctx: IActionContext): Promise<MediaLocation[]> {
    if (input.productId) {
        const product = await ProductsDataActions.getByIdAsync({ callerContext: ctx }, input.productId, input.channelId);
        const newProductRecId = product.ExtensionProperties?.find(ep => ep.Key === 'NewSku')?.Value?.StringValue?.split(':')[0];

        const mediaProductId = newProductRecId && +newProductRecId ? +newProductRecId : input.productId;

        if (ctx.requestContext.app?.config?.OmniChannelMedia) {
            const mediaLocationsInput = new MediaLocationsInput(ctx.requestContext, mediaProductId, input.channelId, input.catalogId);
            return getMediaLocationsForProductAction(mediaLocationsInput, ctx);
        }
        return getMediaLocationsAsync(
            { callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) },
            mediaProductId,
            input.channelId,
            input.catalogId
        ).then(response => {
            return response.map(
                (mediaLocation: MediaLocation): MediaLocation => {
                    mediaLocation.Uri = generateImageUrl(mediaLocation.Uri, ctx.requestContext.apiSettings);
                    mediaLocation.AltText = mediaLocation.AltText ? mediaLocation.AltText : '';
                    return mediaLocation;
                }
            );
        });
    }
    return [];
}

export const getMediaLocationsByIdActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-media-locations-by-id',
    action: <IAction<MediaLocation[] | null>>getMediaLocationsByIdAction,
    input: createMediaLocationsByIdInput
});

export default getMediaLocationsByIdActionDataAction;
